"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import {
  Beef,
  CakeSlice,
  Croissant,
  EggFried,
  Fish,
  Pizza,
  Salad,
  Sandwich,
  Shrimp,
  Star,
  Utensils,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getCenteredHorizontalScrollLeft } from "@/lib/menu-scrollspy";

// Map a data-driven category (by key, then label) to a fitting lucide icon.
// Defaults to UtensilsCrossed when no keyword matches.
function categoryIcon(key: string, label: string): LucideIcon {
  const haystack = `${key} ${label}`.toLowerCase();
  if (/popular|featured|favorite|trending|best/.test(haystack)) return Star;
  if (/pizza/.test(haystack)) return Pizza;
  if (/handheld|burger|sandwich|wrap|panini|sub/.test(haystack)) return Sandwich;
  if (/salad/.test(haystack)) return Salad;
  if (/benedict|brunch_main|egg|omelet/.test(haystack)) return EggFried;
  if (/sweet|pancake|waffle|french toast|pastry|bakery|crepe/.test(haystack))
    return Croissant;
  if (/starter|appetizer|shrimp|small plate/.test(haystack)) return Shrimp;
  if (/steak|beef|burger/.test(haystack)) return Beef;
  if (/seafood|fish|salmon/.test(haystack)) return Fish;
  if (/dessert|cake|sweet treat/.test(haystack)) return CakeSlice;
  if (/pasta|side|main|entree/.test(haystack)) return Utensils;
  return UtensilsCrossed;
}

interface CategoryNavProps {
  categories: readonly { key: string; label: string; icon?: string }[];
  activeCategory: string;
  onCategoryClick: (categoryKey: string) => void;
}

export function CategoryNav({
  categories,
  activeCategory,
  onCategoryClick,
}: CategoryNavProps) {
  const activeButtonRef = useRef<HTMLButtonElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const firstCategoryKey = categories[0]?.key;
  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(maxScroll > 8 && el.scrollLeft < maxScroll - 8);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, categories.length]);

  // Scroll active button into view. Centering the first tab (Most Popular)
  // nudges scrollLeft just enough to show the left fade gradient on top of the
  // active pill — keep the leading tab pinned to the start instead.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const activeButton = activeButtonRef.current;
    if (!scroller || !activeButton) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (activeCategory === firstCategoryKey) {
      scroller.scrollTo({
        left: 0,
        behavior: prefersReduced ? "auto" : "smooth",
      });
    } else {
      const scrollerRect = scroller.getBoundingClientRect();
      const activeButtonRect = activeButton.getBoundingClientRect();
      scroller.scrollTo({
        left: getCenteredHorizontalScrollLeft({
          currentScrollLeft: scroller.scrollLeft,
          itemLeft: activeButtonRect.left,
          itemWidth: activeButtonRect.width,
          containerLeft: scrollerRect.left,
          containerWidth: scrollerRect.width,
        }),
        behavior: prefersReduced ? "auto" : "smooth",
      });
    }

    requestAnimationFrame(updateScrollState);
  }, [activeCategory, firstCategoryKey, updateScrollState]);

  return (
    <nav aria-label="Menu categories" className="relative">
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-white to-transparent transition-opacity duration-300",
          canScrollLeft ? "opacity-100" : "opacity-0"
        )}
      />

      <div
        ref={scrollerRef}
        className="-mx-4 overflow-x-auto scrollbar-hide sm:-mx-6 lg:-mx-8"
      >
        {/* Keep horizontal padding on an inner track so the leading pill is not
            flush against the overflow clip edge (that was clipping the rounded
            left cap and showing a white seam on Most Popular). */}
        <div className="flex w-max gap-1 px-4 py-0.5 snap-x snap-mandatory sm:gap-2 sm:px-6 sm:py-1.5 lg:px-8">
          {categories.map((category) => {
            const isActive = category.key === activeCategory;
            const Icon = categoryIcon(category.key, category.label);
            return (
              <button
                key={category.key}
                ref={isActive ? activeButtonRef : undefined}
                aria-current={isActive ? "true" : undefined}
                onClick={() => onCategoryClick(category.key)}
                className={cn(
                  "inline-flex min-h-9 shrink-0 snap-start items-center whitespace-nowrap rounded-full border px-3 py-1 text-[12px] font-medium transition-colors duration-200 active:scale-95 sm:min-h-11 sm:px-4 sm:py-2 sm:text-sm",
                  isActive
                    ? "border-transparent bg-[#0d0d0d] text-white"
                    : "border-[#e3e3e3] bg-white text-[#575757] hover:border-[#d6d6d6] hover:text-[#0d0d0d]"
                )}
              >
                <Icon className="mr-1 h-3.5 w-3.5 flex-shrink-0 sm:mr-1.5 sm:h-4 sm:w-4" aria-hidden="true" />
                {category.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-white to-transparent transition-opacity duration-300",
          canScrollRight ? "opacity-100" : "opacity-0"
        )}
      />
    </nav>
  );
}
